import React, {useState} from 'react';
import {FiImage, FiSave} from 'react-icons/fi';
import {apiUrl} from '../../config/api';
import {VIDEO_THUMBNAIL_GENERATE, VIDEO_UPDATE} from '../../api/contracts';
import {getApiErrorMessage} from '../../utils/errorMessage';
import './Page.css';
import './VideoThumbnailGenerator.css';

function VideoThumbnailGenerator() {

    const [videoId, setVideoId] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [thumbnailUrl, setThumbnailUrl] = useState(null);
    const [saveLoading, setSaveLoading] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [seekSeconds, setSeekSeconds] = useState(5);

    const handleSubmit = async (e) => {
        e.preventDefault();
        const id = (videoId || '').trim();
        if (!id) {
            setError('Enter a video ID.');
            setThumbnailUrl(null);
            return;
        }
        setError('');
        setThumbnailUrl(null);
        setSaveSuccess(false);
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const headers = {
                'Content-Type': 'application/json',
                ...(token ? {Authorization: `Bearer ${token}`} : {}),
            };
            const res = await fetch(apiUrl(VIDEO_THUMBNAIL_GENERATE.path), {
                method: VIDEO_THUMBNAIL_GENERATE.method,
                headers,
                body: JSON.stringify({video_id: id, seek_seconds: Number(seekSeconds)}),
            });
            const data = await res.json().catch(() => ({}));
            const url = data.data?.thumbnail_url ?? data.thumbnail_url;
            if (res.ok && url) {
                setThumbnailUrl(url);
            } else {
                setError(getApiErrorMessage(data, res.status, {default: 'Could not generate thumbnail. Please try again.'}));
            }
        } catch (err) {
            setError(err.message || 'Unable to connect. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        const id = (videoId || '').trim();
        if (!id || !thumbnailUrl) return;
        setError('');
        setSaveSuccess(false);
        setSaveLoading(true);
        try {
            const token = localStorage.getItem('token');
            const headers = {
                'Content-Type': 'application/json',
                ...(token ? {Authorization: `Bearer ${token}`} : {}),
            };
            const res = await fetch(apiUrl(VIDEO_UPDATE.pathFor(id)), {
                method: VIDEO_UPDATE.method,
                headers,
                body: JSON.stringify({thumbnail_url: thumbnailUrl}),
            });
            const data = await res.json().catch(() => ({}));
            if (res.ok) {
                setSaveSuccess(true);
                setTimeout(() => setSaveSuccess(false), 4000);
            } else {
                setError(getApiErrorMessage(data, res.status, {default: 'Could not save thumbnail. Please try again.'}));
            }
        } catch (err) {
            setError(err.message || 'Unable to connect. Please try again.');
        } finally {
            setSaveLoading(false);
        }
    };

    return (

        <div className="page thumbnail-generator-page">
            <div className="thumbnail-generator-header">
                <h3>Video thumbnail generator</h3>
                <p className="thumbnail-generator-subtitle">
                    Enter a video ID to generate a thumbnail for that video.
                </p>
            </div>

            <form onSubmit={handleSubmit} className="thumbnail-generator-card">
                <div className="thumbnail-generator-row">
                    <label htmlFor="thumbnail-video-id" className="thumbnail-generator-label">
                        Video ID <span className="label-required" aria-hidden="true">*</span>
                    </label>
                    <input
                        id="thumbnail-video-id"
                        type="text"
                        value={videoId}
                        onChange={(e) => setVideoId(e.target.value)}
                        placeholder="e.g. v_001 or video UUID"
                        disabled={loading}
                        className="thumbnail-generator-input"
                        autoComplete="off"
                    />
                </div>
                <div className="thumbnail-generator-seek-row">
                    <label htmlFor="thumbnail-seek-seconds" className="thumbnail-generator-label">
                        Seek Seconds
                    </label>
                    <input
                        id="thumbnail-seek-seconds"
                        type="number"
                        min={5}
                        max={30}
                        value={seekSeconds}
                        onChange={(e) => setSeekSeconds(e.target.value)}
                        onBlur={() => {
                            const val = Math.min(30, Math.max(5, Number(seekSeconds) || 5));
                            setSeekSeconds(val);
                        }}
                        disabled={loading}
                        className="thumbnail-generator-input thumbnail-generator-input--seek"
                    />
                </div>
                <button type="submit" className="thumbnail-generator-btn" disabled={loading}>
                    <FiImage className="thumbnail-generator-btn-icon" aria-hidden/>
                    {loading ? 'Generating…' : 'Generate thumbnail'}
                </button>
                {error && <p className="thumbnail-generator-error" role="alert">{error}</p>}
            </form>

            {saveSuccess && (
                <div className="thumbnail-generator-success" role="status">
                    Thumbnail saved for this video.
                </div>
            )}

            {thumbnailUrl && (
                <div className="thumbnail-generator-result">
                    <h4 className="thumbnail-generator-result-title">Generated thumbnail</h4>
                    <div className="thumbnail-generator-preview-wrap">
                        <img
                            src={thumbnailUrl}
                            alt="Generated thumbnail"
                            className="thumbnail-generator-preview-img"
                        />
                    </div>
                    <p className="thumbnail-generator-result-url" title={thumbnailUrl}>
                        {thumbnailUrl}
                    </p>
                    <button
                        type="button"
                        className="thumbnail-generator-save-btn"
                        onClick={handleSave}
                        disabled={saveLoading}
                    >
                        <FiSave className="thumbnail-generator-btn-icon" aria-hidden/>
                        {saveLoading ? 'Saving…' : 'Save thumbnail for this video'}
                    </button>
                </div>
            )}
        </div>
    );
}

export default VideoThumbnailGenerator;
